'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { useSmoothScroll } from './hooks/useSmoothScroll'
import GrainOverlay from './components/GrainOverlay'
import ScrollProgress from './components/ScrollProgress'
import Navigation from './components/Navigation'
import SoundToggle from './components/SoundToggle'
import Preloader from './components/Preloader'
import ErrorBoundary from './components/ErrorBoundary'
import GlowDivider from './components/GlowDivider'

// Scenes are imported eagerly so they are server-rendered (SSG) — their text
// content lands in the HTML for crawlers, then GSAP animates after hydration.
import Void from './scenes/Void'
import Signal from './scenes/Signal'
import Arsenal from './scenes/Arsenal'
import Path from './scenes/Path'
import Gallery from './scenes/Gallery'
import Proof from './scenes/Proof'
import Voices from './scenes/Voices'
import Portal from './scenes/Portal'
import End from './scenes/End'
import { hasWebGL } from './lib/webgl'

// HexField — heaviest component (Three.js canvas). Client-only (ssr:false).
// Mounted immediately so its shaders/data load DURING the preloader (behind it);
// the particle portrait then assembles in the hero once loading finishes.
const LazyHexField = dynamic(() => import('./components/HexField'), { ssr: false })

// CV dossier viewer — opened from the Arsenal button and the nav console, so it
// lives at the root. Client-only and lazy: pdf.js is only fetched on first open.
const LazyCVViewer = dynamic(() => import('./components/CVViewer'), { ssr: false })

/**
 * App — root component
 * Orchestrates preloader → main content.
 * Lenis smooth scroll + GSAP ScrollTrigger initialized in useSmoothScroll.
 */
export default function App() {
  const [loaded, setLoaded] = useState(false)
  const [hexReady, setHexReady] = useState(false)
  // The Three.js particle field is DESKTOP-ONLY. On phones it's the dominant
  // Core Web Vitals cost (heavy bundle + continuous render loop), so we never
  // mount it there — the hero falls back to the static photo. Starts false so
  // SSR/first paint never include it; resolved client-side below.
  const [isDesktop, setIsDesktop] = useState(false)
  const hexSentinelRef = useRef<HTMLDivElement>(null)

  // Initialize Lenis smooth scroll
  useSmoothScroll()

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    // Particles run only on desktop AND when the user hasn't asked for reduced
    // motion — otherwise the hero is the clean static photo (accessibility + perf).
    const evaluate = () => {
      const enable = mq.matches && !reduceMq.matches && hasWebGL()
      setIsDesktop(enable)
      if (!enable) setHexReady(true) // no shaders to wait for → let the preloader finish
    }
    evaluate()
    mq.addEventListener('change', evaluate)
    reduceMq.addEventListener('change', evaluate)
    return () => {
      mq.removeEventListener('change', evaluate)
      reduceMq.removeEventListener('change', evaluate)
    }
  }, [])

  // HexField (desktop) loads during the preloader and signals readiness; the
  // portrait then assembles in the hero via `started={loaded}`.
  const handleHexReady = useCallback(() => setHexReady(true), [])
  const handlePreloaderComplete = useCallback(() => setLoaded(true), [])

  return (
    <>
      {/* Preloader — holds until the shaders are ready, then reveals the hero */}
      {!loaded && <Preloader onComplete={handlePreloaderComplete} ready={hexReady} />}

      {/* Global overlays */}
      <GrainOverlay />
      <ScrollProgress />
      <Navigation />
      <SoundToggle />
      {/* HexField — DESKTOP ONLY. Skipped on mobile so the Three.js chunk + render
          loop never load there (major Core Web Vitals win); hero shows the photo. */}
      {isDesktop && <LazyHexField onReady={handleHexReady} started={loaded} />}
      <div ref={hexSentinelRef} className="absolute top-0 left-0 w-0 h-0" aria-hidden="true" />

      {/* CV dossier — renders nothing until opened */}
      <LazyCVViewer />

      {/* Skip to content */}
      <a
        href="#void"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-999 focus:px-4 focus:py-2 focus:bg-fire focus:text-void focus:font-mono focus:text-sm"
      >
        Skip to content
      </a>

      {/* Main content — rendered immediately so LCP images are in-DOM from first paint;
          the preloader overlay (z-index 10000) covers until ready */}
      <main
        id="main"
        aria-label="Portfolio content"
        style={loaded ? undefined : { visibility: 'hidden', pointerEvents: 'none' }}
      >
        <ErrorBoundary>
          <Void isReady={loaded} />
          <GlowDivider />
          <Signal />
          <Arsenal />
          <Path />
          <Gallery />
          <Proof />
          <Voices />
          <Portal />
          <End />
        </ErrorBoundary>
      </main>
    </>
  )
}
